/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-controls-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import AdminRisksGrid from './admin-risks-grid';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useTranslation, useGridCells } from 'hooks';
import { handleTimeInDates, reorderArray } from 'utils/filter';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2/index';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

const AdminControlsGrid = ({ items, rowActions, editRisk, unassignRisk, isSystemLevel, canEdit, className }: any) => {
    const { t } = useTranslation();

    const { MaxLengthCell, DateCell, YesNoCell, CustomizableCell, ActiveInactiveCell } = useGridCells();
    const [data, setData] = useState(null);

    useEffect(() => {
        let filteredData = [...items].map((item: any) => ({
            ...item,
            threshold: item.threshold || '',
            action: item.action || '',
        }));

        // we need to have this logic when we have two fields for sorting which are dates or when the first sorting field is date
        let arrayToSort = filteredData.slice();
        arrayToSort = handleTimeInDates(arrayToSort, ['updated']);
        arrayToSort =
            orderBy(arrayToSort, [
                { field: 'active', dir: 'desc' },
                { field: 'updated', dir: 'desc' },
            ]) ?? [];
        reorderArray(filteredData, arrayToSort);
        filteredData = restructuredUpdatedCreatedBy(arrayToSort);
        // end of logic
        setData(filteredData);
    }, [items]);

    return (
        <ExpandableGridV2
            title={t('ra:controls')}
            className={className}
            customKey="admin-controls-grid"
            id="admin-controls-grid"
            items={data}
            groupable
            hideCheckbox
            showPager
            scrollable="scrollable"
            reorderable
            resizable
            pageable={false}
            rowActions={rowActions}
            detail={(item: any) => {
                if (item.associatedRisks?.length) {
                    return (
                        <div className="child-grid-wrap monitoring-grid-child">
                            <AdminRisksGrid
                                title={t('ra:risks')}
                                isChild
                                isSystemLevel={isSystemLevel}
                                data={item.associatedRisks}
                                rowActions={(risk: any) => [
                                    {
                                        label: 'ra:editRisk',
                                        icon: ['fal', 'pen-to-square'],
                                        enabled: canEdit,
                                        onClick: () => {
                                            editRisk(risk);
                                        },
                                    },
                                    {
                                        label: 'ra:unassignRisk',
                                        enabled: canEdit,
                                        icon: ['fal', 'link-slash'],
                                        onClick: () => {
                                            unassignRisk(risk, item);
                                        },
                                    },
                                ]}
                                showPager
                                showDetail={false}
                            />
                        </div>
                    );
                }
                return null;
            }}
            columns={[
                {
                    label: t('ra:risks'),
                    field: 'numberOfRisks',
                    id: 'control-numberOfRisks',
                    minWidth: 140,
                    lockedByDefault: true,
                    filter: 'numeric',
                },
                {
                    label: t('ra:description'),
                    id: 'control-description',
                    field: 'description',
                    minWidth: 250,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(150, '30vh') },
                },
                ...(!isSystemLevel
                    ? [
                          {
                              label: t('ra:phase'),
                              id: 'control-phase',
                              minWidth: 200,
                              field: 'controlPhase.name',
                              cells: {
                                  data: ActiveInactiveCell(
                                      (dataItem: any) => dataItem.controlPhase?.active === false,
                                      (dataItem: any) => dataItem.controlPhase?.name,
                                  ),
                              },
                          },
                          {
                              label: t('admin:controlType'),
                              id: 'control-type',
                              minWidth: 200,
                              field: 'controlType.name',
                              cells: {
                                  data: ActiveInactiveCell(
                                      (dataItem: any) => dataItem.controlType?.active === false,
                                      (dataItem: any) => dataItem.controlType?.name,
                                  ),
                              },
                          },
                      ]
                    : []),
                {
                    label: t('ra:threshold'),
                    id: 'control-threshold',
                    field: 'threshold',
                    minWidth: 180,
                    cells: { data: MaxLengthCell(150, '30vh') },
                },
                {
                    label: t('ra:actionToBeTaken'),
                    id: 'control-action-to-be-taken',
                    minWidth: 260,
                    field: 'action',
                    cells: { data: MaxLengthCell(150, '30vh') },
                },
                ...(!isSystemLevel
                    ? [
                          {
                              label: t('ra:responsibleFunction'),
                              id: 'control-responsible-func',
                              field: 'responsibleFunction.name',
                              minWidth: 260,
                              cells: {
                                  data: ActiveInactiveCell(
                                      (dataItem: any) => dataItem.responsibleFunction?.active === false,
                                      (dataItem: any) => dataItem.responsibleFunction?.name,
                                  ),
                              },
                          },
                      ]
                    : []),
                {
                    label: t('ra:reviewFrequency'),
                    id: 'control-review-frequency',
                    minWidth: 220,
                    field: '_reviewFrequency',
                    cells: { data: CustomizableCell((dataItem: any) => dataItem._reviewFrequency || '') },
                },
                {
                    label: t('active'),
                    id: 'control-active',
                    field: 'active',
                    minWidth: 140,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:createdBy'),
                    id: 'control-createdby',
                    field: '_createdByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    id: 'control-created',
                    minWidth: 200,
                    field: 'created',
                    cells: { data: DateCell },
                    filter: 'date',
                    filterUi: GridColumnDateFilter,
                },
                {
                    label: t('ra:updatedBy'),
                    id: 'control-updatedby',
                    field: '_updatedByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'control-updated',
                    minWidth: 200,
                    filter: 'date',
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilter,
                },
                {
                    field: 'recordId',
                    id: 'control-id',
                    showByDefault: false,
                    label: 'ID',
                    filter: 'numeric',
                    minWidth: 230,
                },
            ]}
        />
    );
};

export default AdminControlsGrid;
